import { set } from 'lodash';
import { useCallback } from 'react';
import { animated } from 'react-spring';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import type { Theme } from '@hivebrite/design-system/Theme';

import {
  useBuilderContext,
  useComponentContext,
  useComponentsContext,
} from 'hb-builder/hooks';
import { createNewItem } from 'hb-builder/utils/createNewItem';

import DynamicLayout from './DynamicLayout';

const AnimatedBox = animated(Box);

export type Layout = 1 | 2 | 3 | 4;
export type Proportions = number[];

export interface DynamicLayoutToolbarProps {
  styles?: {
    opacity: number;
    pointerEvents: string;
    x: any;
  };
}

const DynamicLayoutToolbar = ({ styles }: DynamicLayoutToolbarProps) => {
  const { updateItem, getPath } = useBuilderContext();
  const { item } = useComponentContext();
  const { components } = useComponentsContext();

  const updateColumnWidth = ({ column, finalProportions, index }) => ({
    ...column,
    attributes: {
      ...column.attributes,
      width: finalProportions[index] * 100,
    },
  });

  const updateOnlyColumnsWidth = ({ children, layout, finalProportions }) =>
    children.reduce((result, column, index) => {
      // Update column width
      if (index < layout) {
        return [
          ...result,
          updateColumnWidth({ column, finalProportions, index }),
        ];
      }

      const last = result[result.length - 1];

      // Retrieve children of the current column which should be deleted to put it the last untouched column
      last.children = [...last.children, ...column.children];

      // Modify last "allowed" column with the previous retrieved children
      result[result.length - 1] = last;

      return result;
    }, []);

  const reduceNumberOfColumns = ({ children, layout, finalProportions }) =>
    children.reduce((result, column, index) => {
      // Only update column width for the ones that shouldn't be deleted
      if (index < layout) {
        result.push(updateColumnWidth({ column, finalProportions, index }));

        return result;
      }

      const last = result[result.length - 1];

      // Retrieve children of the current column which should be deleted to put it the last untouched column
      last.children = [...last.children, ...column.children];

      // Modify last "allowed" column with the previous retrieved children
      result[result.length - 1] = last;

      return result;
    }, []);

  const getShouldProportionUpdate = ({
    item,
    finalProportions,
    shouldNumberOfColumnsChange,
  }) =>
    !shouldNumberOfColumnsChange &&
    item?.children?.filter(
      (child, i) =>
        parseFloat(child.attributes?.width) !== finalProportions[i] * 100,
    ).length > 0;

  const handleChangeLayout = useCallback(
    ({
        layout: explicitLayout,
        proportions,
      }: {
        layout?: Layout;
        proportions?: Proportions;
      }) =>
      (e) => {
        e.preventDefault();
        e.stopPropagation();

        const layout = explicitLayout || proportions?.length || 1;
        const finalProportions = proportions || Array(layout).fill(1 / layout);

        updateItem({
          path: getPath(item.id),
          updater: (item) => {
            const currentNbColumns = item.children.length;
            const shouldNumberOfColumnsChange = currentNbColumns !== layout;
            const shouldProportionUpdate = getShouldProportionUpdate({
              item,
              finalProportions,
              shouldNumberOfColumnsChange,
            });

            if (!shouldNumberOfColumnsChange && shouldProportionUpdate) {
              item.children = updateOnlyColumnsWidth({
                children: item.children,
                layout,
                finalProportions,
              });
            }

            if (layout > currentNbColumns) {
              const newColumns = Array(layout - currentNbColumns)
                .fill(undefined)
                .map(() => createNewItem(components.Column.item({})));

              const columns = [...item.children, ...newColumns];

              item.children = columns.map((column, index) =>
                updateColumnWidth({ column, finalProportions, index }),
              );
            }

            if (layout < currentNbColumns) {
              item.children = reduceNumberOfColumns({
                children: item.children,
                layout,
                finalProportions,
              });
            }

            item.children.forEach((column) => {
              set(
                column,
                'attributes.verticalAlign',
                item.attributes?.verticalAlign,
              );
            });

            return item;
          },
        });
      },
    [components, item],
  );

  return (
    <WrapperPopover
      data-testid="dynamic-layout-toolbar"
      style={
        styles
          ? {
              opacity: styles.opacity,
              pointerEvents: styles.pointerEvents,
              transform: styles.x.to((x) => `translate3d(${x}px, -50%, 0)`),
            }
          : {}
      }
    >
      <Box flexDirection="row">
        <DynamicLayout layout={1} onClick={handleChangeLayout({ layout: 1 })} />
        <DynamicLayout
          layout={2}
          proportions={[1 / 2, 1 / 2]}
          onClick={handleChangeLayout({ layout: 2 })}
        />
        <DynamicLayout
          layout={2}
          proportions={[1 / 3, 2 / 3]}
          onClick={handleChangeLayout({ proportions: [1 / 3, 2 / 3] })}
        />
        <DynamicLayout
          layout={2}
          proportions={[2 / 3, 1 / 3]}
          onClick={handleChangeLayout({ proportions: [2 / 3, 1 / 3] })}
        />
        <DynamicLayout layout={3} onClick={handleChangeLayout({ layout: 3 })} />
        <DynamicLayout layout={4} onClick={handleChangeLayout({ layout: 4 })} />
      </Box>
    </WrapperPopover>
  );
};

export default DynamicLayoutToolbar;

interface DynamicLayoutToolbarStylesProps {
  $layout?: Layout;
  theme: Theme;
}

const WrapperPopover = styled(AnimatedBox)<DynamicLayoutToolbarStylesProps>`
  background-color: inherit;
  margin-right: ${({ theme }) => `${theme.space[2]}px`};
  left: 50%;
  top: calc(100% + 8px);
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  margin-right: 0;
  padding: 4px 2px;
  transform: translate3d(-50%, 0, 0);
  z-index: 100;
  /* Needed to ensure the toolbar can be accessed on hover (as it's outside the parent wrapper) */
  &:before {
    bottom: 0;
    content: '';
    position: absolute;
    right: 0;
    top: -8px;
    width: 100%;
  }
  position: absolute;
  user-select: none;
  z-index: ${({ theme }) => theme.zIndices.sticky};
`;
